import { Fragment } from "react";
import { type SxProps, type Theme } from "@mui/material/styles";

import SidebarMenuItem from "./sidebar-menu-item";
import { Box } from "ui/box";
import { Divider } from "ui/divider";
import { Tooltip } from "ui/tooltip";
import {
  SidebarMenuItem as SidebarMenuItemType,
  SidebarMenuSection as SidebarMenuSectionType,
} from "./sidebar-menu.types";
import { useSidebar } from "./sidebar-context";

export type SidebarMenuSectionProps = {
  section: SidebarMenuSectionType;
  activeItemId?: string;
  hasActiveItemIdMatch: boolean;
  onItemSelect?: (item: SidebarMenuItemType) => void;
  isAnyNestedMenuOpen?: boolean;
  onNestedMenuOpenChange?: (itemId: string, isOpen: boolean) => void;
};

const ITEM_TOOLTIP_WRAPPER_SX: SxProps<Theme> = { display: "block", width: "100%" };

/** Padding on top of the list's `space-y-1` makes Figma's 8px either side of the line. */
const DIVIDER_WRAPPER_SX: SxProps<Theme> = { py: 0.5 };

const DIVIDER_SX: SxProps<Theme> = (theme) => ({ borderColor: theme.palette.border.tertiary });

const SidebarMenuSection = ({
  section,
  activeItemId,
  hasActiveItemIdMatch,
  onItemSelect,
  isAnyNestedMenuOpen = false,
  onNestedMenuOpenChange,
}: SidebarMenuSectionProps) => {
  const { isCollapsed } = useSidebar();

  return (
    <div className="mb-1">
      <div className="space-y-1">
        {section.items.map((item, index) => (
          <Fragment key={item.id}>
            {item.hasDividerBefore && index > 0 && (
              <Box sx={DIVIDER_WRAPPER_SX}>
                <Divider sx={DIVIDER_SX} />
              </Box>
            )}
            <Tooltip
              label={item.label}
              placement="right"
              disabled={!isCollapsed}
              suppressed={isCollapsed && isAnyNestedMenuOpen}
              wrapperSx={ITEM_TOOLTIP_WRAPPER_SX}>
              <div className="w-full">
                <SidebarMenuItem
                  item={item}
                  activeItemId={activeItemId}
                  hasActiveItemIdMatch={hasActiveItemIdMatch}
                  onItemSelect={onItemSelect}
                  onNestedMenuOpenChange={onNestedMenuOpenChange}
                />
              </div>
            </Tooltip>
          </Fragment>
        ))}
      </div>
    </div>
  );
};

export default SidebarMenuSection;
